/**
 * Chat API client -- typed fetch functions for all /chats endpoints.
 *
 * Uses the shared apiClient (api-client.ts) for auth headers, error parsing,
 * and request IDs. All functions are pure and stateless.
 */

import { apiClient } from '@/services/api-client';
import type {
  ChatArtifact,
  ChatSummary,
  ChatDetailed,
  Message,
  SearchResult,
  CreateChatInput,
  UpdateChatInput,
  SearchChatsInput,
  ListChatsParams,
  ListMessagesParams,
  PaginatedResponse,
} from '@/types/chat.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQueryString(
  params: object,
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null,
  );
  if (entries.length === 0) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of entries) {
    qs.set(key, String(value));
  }
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const BASE = '/v1/chats';

/**
 * List user's chats with cursor-based pagination.
 */
export async function fetchChats(
  params?: ListChatsParams,
): Promise<PaginatedResponse<ChatSummary>> {
  const qs = buildQueryString(params ?? {});
  return apiClient.get<PaginatedResponse<ChatSummary>>(`${BASE}${qs}`);
}

/**
 * Get a single chat with its first page of messages.
 */
export async function fetchChat(id: string): Promise<ChatDetailed> {
  return apiClient.get<ChatDetailed>(`${BASE}/${id}`);
}

/**
 * Create a new chat (optionally with a first message).
 */
export async function createChat(
  input: CreateChatInput,
): Promise<ChatDetailed> {
  return apiClient.post<ChatDetailed>(BASE, input);
}

/**
 * Update chat metadata (rename, pin, archive, change model/agent).
 */
export async function updateChat(
  id: string,
  input: UpdateChatInput,
): Promise<ChatSummary> {
  return apiClient.patch<ChatSummary>(`${BASE}/${id}`, input);
}

/**
 * Soft-delete a chat.
 */
export async function deleteChat(id: string): Promise<void> {
  return apiClient.delete<void>(`${BASE}/${id}`);
}

/**
 * Fetch messages for a chat with cursor-based pagination.
 */
export async function fetchMessages(
  chatId: string,
  params?: ListMessagesParams,
): Promise<PaginatedResponse<Message>> {
  const qs = buildQueryString(params ?? {});
  return apiClient.get<PaginatedResponse<Message>>(
    `${BASE}/${chatId}/messages${qs}`,
  );
}

/**
 * Search across user's chats (text + semantic).
 */
export async function searchChats(
  input: SearchChatsInput,
  init?: RequestInit,
): Promise<{ results: SearchResult[] }> {
  return apiClient.post<{ results: SearchResult[] }>(`${BASE}/search`, input, init);
}

// ---------------------------------------------------------------------------
// Chat Artifacts
// ---------------------------------------------------------------------------

/**
 * Fetch artifacts generated during a chat.
 */
export async function fetchChatArtifacts(
  chatId: string,
): Promise<ChatArtifact[]> {
  return apiClient.get<ChatArtifact[]>(`${BASE}/${chatId}/artifacts`);
}

// ---------------------------------------------------------------------------
// Export chat
// ---------------------------------------------------------------------------

export type ExportFormat = 'markdown' | 'json';

/**
 * Export a chat as markdown or JSON. Returns a Blob for client-side download.
 */
export async function exportChat(
  id: string,
  format: ExportFormat = 'markdown',
): Promise<Blob> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  const token = localStorage.getItem('opus-ia-token');
  const response = await fetch(`${baseUrl}/v1/chats/${id}/export?format=${format}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'x-request-id': `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Export failed: ${response.status}`);
  }
  return response.blob();
}

// ---------------------------------------------------------------------------
// Regenerate message
// ---------------------------------------------------------------------------

export interface RegenerateMessageResponse {
  cancelled_message_ids: string[];
  new_message_id: string;
  stream_url: string;
}

/**
 * Regenerate an assistant message (forks at parent).
 * Returns the new message ID and stream URL for consuming the new response.
 */
export async function regenerateMessage(
  chatId: string,
  messageId: string,
  options?: { model_id?: string },
): Promise<RegenerateMessageResponse> {
  return apiClient.post<RegenerateMessageResponse>(
    `${BASE}/${chatId}/messages/${messageId}/regenerate`,
    options ?? {},
  );
}

// ---------------------------------------------------------------------------
// Branch from message
// ---------------------------------------------------------------------------

/**
 * Create a new chat branching from a specific message.
 * Copies all messages up to and including the branch point.
 */
export async function branchFromMessage(
  chatId: string,
  messageId: string,
  options?: { title?: string },
): Promise<ChatDetailed> {
  return apiClient.post<ChatDetailed>(
    `${BASE}/${chatId}/messages/${messageId}/branch`,
    options ?? {},
  );
}
